// src/belediye/services/closeApprovalApi.js
// Close Approval API (close_approval_app)
// - belediyeApi instance'ını reuse eder (token refresh + unit header davranışı korunur)
// - Feature flag "probe" için 404 => disabled, 200 => enabled
//
// Debug:
// - .env => REACT_APP_DEBUG_CLOSE_APPROVAL=1
// - Konsolda [closeApprovalApi] prefix'i ile log basar.

import belediyeApi from "./belediyeApi";

const BASE = "/close-approval/close-requests";

// ------------------------------------------------------------
// Debug helper
// ------------------------------------------------------------
const DEBUG =
  String(process.env.REACT_APP_DEBUG_CLOSE_APPROVAL || "").trim() === "1";

const log = (...args) => {
  if (DEBUG) console.log("[closeApprovalApi]", ...args);
};

const warn = (...args) => {
  if (DEBUG) console.warn("[closeApprovalApi]", ...args);
};

// undefined değerleri query/body’den temizle
const stripUndefined = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
};

// ------------------------------------------------------------
// Feature Flag Probe
// ------------------------------------------------------------

/**
 * Modül açık mı kapalı mı?
 * Backend davranışı:
 * - module disabled => 404 NotFound (endpoint yokmuş gibi)
 * - enabled => 200
 *
 * @returns {Promise<boolean>}
 */
export const probeCloseApprovalEnabled = async () => {
  try {
    log("probeCloseApprovalEnabled:start");
    await belediyeApi.get(`${BASE}/notifications/`, { params: { limit: 1 } });
    log("probeCloseApprovalEnabled:enabled");
    return true;
  } catch (err) {
    const status = err?.response?.status;
    warn("probeCloseApprovalEnabled:error", { status, err });
    if (status === 404) return false;
    // diğer hatalarda fail-safe: kapalı kabul et
    return false;
  }
};

// ------------------------------------------------------------
// CloseRequest CRUD
// ------------------------------------------------------------

/**
 * Listeleme (başkan/müdür paneli için)
 * Backend destekliyorsa params ile filtrelenir (status, unit_id vs).
 */
export const listCloseRequests = async (params = {}) => {
  const finalParams = stripUndefined(params);
  log("listCloseRequests:start", finalParams);

  const { data } = await belediyeApi.get(`${BASE}/`, { params: finalParams });

  // data genelde list serializer array döner
  log("listCloseRequests:ok", {
    count: Array.isArray(data) ? data.length : undefined,
  });

  return data;
};

/**
 * Detail (canonical)
 * GET /close-requests/:id/
 */
export const getCloseRequestDetail = async (id) => {
  if (!id) throw new Error("closeRequest id zorunlu");
  log("getCloseRequestDetail:start", { id });

  const { data } = await belediyeApi.get(`${BASE}/${id}/`);

  log("getCloseRequestDetail:ok", { id: data?.id, status: data?.status });
  return data;
};

/**
 * Backward-compatible alias
 * Bazı yerlerde retrieveCloseRequest import ediliyor olabilir.
 */
export const retrieveCloseRequest = async (id) => {
  return await getCloseRequestDetail(id);
};

/**
 * Create (personel)
 * payload: { application_id, proposed_status, proposed_note?, internal_note? }
 */
export const createCloseRequest = async (payload) => {
  const body = stripUndefined({
    application_id: payload?.application_id,
    proposed_status: payload?.proposed_status, // "çözüldü" | "çözülemedi"
    proposed_note: payload?.proposed_note ?? "",
    internal_note: payload?.internal_note ?? "",
  });

  if (!body.application_id) throw new Error("application_id zorunlu");
  if (!body.proposed_status) throw new Error("proposed_status zorunlu");

  log("createCloseRequest:start", {
    application_id: body.application_id,
    proposed_status: body.proposed_status,
  });

  const { data } = await belediyeApi.post(`${BASE}/`, body);

  log("createCloseRequest:ok", { id: data?.id, status: data?.status });
  return data;
};

/**
 * Cancel (pending)
 * POST /close-requests/:id/cancel/
 * payload: { note?, internal_note? }
 */
export const cancelCloseRequest = async (id, payload = {}) => {
  if (!id) throw new Error("closeRequest id zorunlu");

  const body = stripUndefined({
    note: payload?.note ?? "",
    internal_note: payload?.internal_note ?? "",
  });

  log("cancelCloseRequest:start", { id });

  const { data } = await belediyeApi.post(`${BASE}/${id}/cancel/`, body);

  log("cancelCloseRequest:ok", { id: data?.id, status: data?.status });
  return data;
};

/**
 * Decide (approve/reject)
 * POST /close-requests/:id/decide/
 * payload:
 *  {
 *    decision: "approve" | "reject" | "onayla" | "reddet" | ...
 *    final_status?, final_note?, decision_note?, internal_note?
 *  }
 */
export const decideCloseRequest = async (id, payload = {}) => {
  if (!id) throw new Error("closeRequest id zorunlu");

  const body = stripUndefined({
    decision: payload?.decision, // approve / reject / onayla / reddet ...
    final_status: payload?.final_status, // optional
    final_note: payload?.final_note, // optional
    decision_note: payload?.decision_note ?? "",
    internal_note: payload?.internal_note ?? "",
  });

  if (!body.decision) throw new Error("decision zorunlu");

  log("decideCloseRequest:start", { id, decision: body.decision });

  const { data } = await belediyeApi.post(`${BASE}/${id}/decide/`, body);

  log("decideCloseRequest:ok", { id: data?.id, status: data?.status });
  return data;
};

// ------------------------------------------------------------
// In-app Notifications
// ------------------------------------------------------------

// NOT (FAZ-50 temizliği): `getCloseApprovalNotifications` ve
// `markCloseApprovalNotificationsRead` KALDIRILDI — bildirimler artık merkezî
// `bildirim_app`'ten okunuyor (services/bildirimApi.js). Legacy uçların kendisi
// backend'de duruyor: `notifications` ucu hâlâ `probeCloseApprovalEnabled`
// tarafından modül-açık yoklaması olarak kullanılıyor (bkz. yukarısı).

// ------------------------------------------------------------
// Convenience: "bu başvurunun talebi var mı?"
// ------------------------------------------------------------

/**
 * Uygulama bazında talep(leri) çekmek için yardımcı.
 *
 * İDEAL (backend patch ile):
 *   GET /close-requests/?application_id=164&status=pending
 *
 * ŞU AN (backend filtre yoksa):
 *   - status=pending gibi filtrelerle liste çekip client-side match yapılır.
 *   - Slice bu listeden application ile eşleşeni bulup detail çekmelidir.
 */
export const fetchCloseRequestsForApplication = async (
  applicationId,
  params = {}
) => {
  if (!applicationId) throw new Error("applicationId zorunlu");

  const finalParams = stripUndefined({
    ...params,
    // backend desteklerse aç:
    // application_id: applicationId,
  });

  log("fetchCloseRequestsForApplication:start", {
    applicationId,
    params: finalParams,
  });

  // Şimdilik list üzerinden
  const data = await listCloseRequests(finalParams);

  log("fetchCloseRequestsForApplication:ok", {
    applicationId,
    count: Array.isArray(data) ? data.length : undefined,
  });

  return data;
};
